import { useEffect } from "react";

export interface SeoConfig {
  title: string;
  description: string;
  keywords?: string;
  path: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const JSON_LD_ID = "site-jsonld";

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  if (!content) return;
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function buildAbsoluteUrl(path: string) {
  const rawBase = typeof __BASE_PATH__ === "string" ? __BASE_PATH__ : "/";
  const base = rawBase.endsWith("/") ? rawBase.slice(0, -1) : rawBase;
  const normalized = path.startsWith("/") ? path : `/${path}`;
  const suffix = normalized === "/" ? "" : normalized;
  return `${window.location.origin}${base}${suffix}`;
}

export default function useSeo(config: SeoConfig) {
  const { title, description, keywords, path, jsonLd } = config;
  const jsonLdText = jsonLd ? JSON.stringify(jsonLd) : "";
  const url = buildAbsoluteUrl(path);

  useEffect(() => {
    document.title = title;
    upsertMeta("name", "description", description);
    if (keywords) upsertMeta("name", "keywords", keywords);
    upsertMeta("name", "robots", "index,follow,max-image-preview:large");
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", "15黑料网");
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertLink("canonical", url);

    let script = document.getElementById(JSON_LD_ID) as HTMLScriptElement | null;
    if (jsonLdText) {
      if (!script) {
        script = document.createElement("script");
        script.id = JSON_LD_ID;
        script.type = "application/ld+json";
        document.head.appendChild(script);
      }
      script.textContent = jsonLdText;
    } else if (script) {
      script.textContent = "";
    }
  }, [title, description, keywords, url, jsonLdText]);
}